<template>
  <div :class="{ 'main-body': true, 'popup-mode': isPopupMode }">
    <div class="title-bar">
      <h1>Find a Contractor</h1>
      <div class="actions">
        <div class="primary-input" style="width: 20rem">
          <input type="text" id="lookupInput" class="no-outline" v-model="searchText" @keyup.enter="search" />
          <label>City, Zip, Or Address</label>
        </div>
        <Dropdown
          label="Contractors"
          :options="[
            { text: 'Certified Only', value: false },
            { text: 'All Contractors', value: true },
          ]"
          class="primary-input"
          style="min-width: 12rem; z-index: 1000"
          v-model="searchAll" />
        <button :class="{ 'square-btn': true, saving: isSearching }" @click="search()" :disabled="!searchText.length || isSearching">Search<img src="../assets/spinner.svg" /></button>
        <img v-if="isPopupMode" src="../assets/close.svg" class="find-contractor-popup-close" @click="$emit('close')" />
      </div>
    </div>
    <div class="scrollable-div display-scrollbar">
      <table class="full-width-table">
        <tbody>
          <tr class="table-header">
            <th style="width: 1.5rem"></th>
            <th style="width: 20%">Name</th>
            <th style="width: 30%">Address</th>
            <th style="width: 15%">Phone</th>
            <th style="width: 25%">Email</th>
            <th style="width: 10%">Distance</th>
          </tr>
          <tr v-for="contractor in contractors">
            <td class="selection-column">
              <div v-if="!isPopupMode" class="clipboard-icon-container" @click="copyToClipboard(contractor)">
                <img src="../assets/clipboard.svg" id="noHover" />
                <img src="../assets/clipboard-hover.svg" id="hover" />
              </div>
              <Checkbox v-else v-model="selectedContractors[contractor.id]" @update:modelValue="updateSelectedContractors" />
            </td>
            <td class="link">
              <a @click="navigateToContractor(contractor.id)">{{ contractor.name }}</a>
            </td>
            <td>{{ contractor.line1 }}, {{ contractor.city }}, {{ contractor.state }} {{ contractor.zip }}</td>
            <td>{{ contractor.phone }}</td>
            <td class="link">
              <a @click="sendEmail(contractor)">{{ contractor.email }}</a>
            </td>
            <td>{{ contractor.distance }}mi</td>
          </tr>
          <tr v-if="contractors.length === 0" class="no-results">
            <td colspan="6">Enter address to find contractors</td>
          </tr>
        </tbody>
      </table>
      <textarea id="copyElement" ref="copyElement"></textarea>
    </div>
  </div>
</template>

<script>
import useContractorQuery from '@/composables/useContractorQuery';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useUpdateLocation from '@/composables/useUpdateLocation';
import useCurrentUser from '@/composables/useCurrentUser';
import { ref, reactive } from 'vue';
import useAlerts from '@/composables/useAlerts';
import Dropdown from '@/components/inputs/Dropdown.vue';
import Checkbox from '@/components/inputs/Checkbox.vue';

export default {
  name: 'FindContractor',
  emits: ['close', 'update:modelValue'],
  props: {
    isPopupMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    modelValue: {
      type: Array,
      required: false,
      default: () => [],
    },
    initialAddress: {
      type: String,
      required: false,
      default: '',
    },
  },
  components: {
    Dropdown,
    Checkbox,
  },
  setup() {
    const searchAll = ref(false);
    const { load, refetch } = useContractorQuery();
    const { decryptSymbols } = useDecryptSymbols();
    const { updateLocation } = useUpdateLocation();
    const { user } = useCurrentUser();
    const { addAlert } = useAlerts();

    async function getContractors() {
      const variables = reactive({
        searchAll: searchAll.value,
      });
      try {
        const loadResults = await load(null, variables);
        if (loadResults) return loadResults;
        const refetchResults = await refetch(variables);
        if (refetchResults) return refetchResults.data;
        return [];
      } catch (error) {
        console.error(error);
        addAlert('Error searching for contractors. Please try again', 'high', 4);
      }
    }

    return {
      searchAll,
      getContractors,
      decryptSymbols,
      updateLocation,
      user,
      addAlert,
    };
  },
  data() {
    return {
      searchText: '',
      contractors: [],
      isSearching: false,
      selectedContractors: {},
    };
  },
  methods: {
    navigateToContractor(id) {
      this.$router.push({ name: 'companies', params: { companyid: id } });
    },
    sendEmail(contractor) {
      window.location.href = `mailto:${contractor.email}?subject=${encodeURIComponent('NEW LEAD FROM WESTERN INTERLOCK')}&body=${encodeURIComponent(
        'Hello! We just sent your contact information to the following customer for a quote. Please let us know if you have any questions.',
      )}%0D%0A%0D%0A${encodeURIComponent('the sales team.')}`;
    },
    async search() {
      if (!this.searchText || this.isSearching) return;
      this.isSearching = true;
      try {
        const { contractorCompanies } = await this.getContractors();
        const formatContractors = this.formatContractors(contractorCompanies);
        const locatedContractors = await this.locateContractors(formatContractors);
        const addDistanceContractors = await this.getDistanceToContractors(locatedContractors);
        const sortedContractors = addDistanceContractors.sort((a, b) => a.distance - b.distance);
        const trimmedContractors = sortedContractors.slice(0, 50);
        this.contractors = trimmedContractors;
        this.resetSelectedContractors();
      } catch (error) {
        console.error(error);
      }
      this.isSearching = false;
    },
    formatContractors(companies) {
      let mappedCompanies = companies.map((company) => {
        const address = company.address || {};
        const phone = company.phones.find((phone) => phone.TYPEID === 'C3AE9586-6158-4D0D-9FAF-253CB0152F18');
        let email = '';
        const contacts = company.contacts.filter((contact) => {
          return !contact.INACTIVE && contact.email && contact.email.ADDRESS;
        });
        const contact = contacts.find((contact) => contact.SALESCONTACT === true);
        if (!contact && contacts.length) {
          email = contacts[0].email.ADDRESS;
        } else if (contact) {
          email = contact.email.ADDRESS;
        }

        return {
          id: company.COMPANYID,
          name: this.decryptSymbols(company.NAME),
          line1: this.decryptSymbols(address.LINE1 || ''),
          line2: this.decryptSymbols(address.LINE2 || ''),
          city: this.decryptSymbols(address.CITY || ''),
          state: this.decryptSymbols(address.STATE || ''),
          zip: this.decryptSymbols(address.POSTALCODE || ''),
          address: `${this.decryptSymbols(address.LINE1 || '')}, ${this.decryptSymbols(address.CITY || '')}, ${this.decryptSymbols(address.STATE || '')} ${this.decryptSymbols(address.POSTALCODE || '')}`,
          phone: phone ? phone.NUMBERDISPLAY : '',
          certified: company.CERTIFIEDCONTRACTOR,
          lat: company.LAT === 0 ? 0 : company.LAT || null,
          lng: company.LNG === 0 ? 0 : company.LNG || null,
          email: this.decryptSymbols(email),
        };
      });
      return mappedCompanies.filter((company) => company.id && company.name && company.line1 && (company.city || company.zip));
    },
    async locateContractors(companies) {
      const locatedCompanies = [];
      const newlyLocatedCompanies = [];

      // Define an async function to handle location lookup for each company
      async function locateCompany(company) {
        if ((company.lat === 0 && company.lng === 0) || company.lat || company.lng) {
          locatedCompanies.push(company);
          return company;
        } else {
          const apiConn = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(company.address)}&key=AIzaSyCUZQ9BxAu2zYN2MlxQs8pwLkU202MIXGQ`;
          const response = await fetch(apiConn);
          const completeResult = await response.json();
          const results = completeResult.results;

          if (results.length) {
            company.lat = results[0].geometry.location.lat;
            company.lng = results[0].geometry.location.lng;
          } else {
            company.lat = 0;
            company.lng = 0;
          }

          locatedCompanies.push(company);
          newlyLocatedCompanies.push(company);
          return company;
        }
      }

      // Asynchronously process each company
      await Promise.all(companies.map(async (company) => await locateCompany(company)));

      newlyLocatedCompanies.forEach(async (company) => {
        this.updateLocation(company.id, company.lat, company.lng);
      });

      // Return the arrays of located companies
      return locatedCompanies;
    },
    async getDistanceToContractors(companies) {
      let userLat = 0;
      let userLng = 0;

      const apiConn = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(this.searchText)}&key=AIzaSyCUZQ9BxAu2zYN2MlxQs8pwLkU202MIXGQ`;
      const response = await fetch(apiConn);
      const completeResponse = await response.json();
      const results = completeResponse.results;

      if (results.length) {
        userLat = results[0].geometry.location.lat;
        userLng = results[0].geometry.location.lng;
      } else {
        return [];
      }

      return companies.map((company) => {
        const distance = this.calculateDistance([userLat, userLng], [company.lat, company.lng]);
        return { ...company, distance: distance };
      });
    },
    calculateDistance(coord1, coord2) {
      /**
       * Calculate the distance between two GPS coordinates using the Haversine formula.
       *
       * @param {Array} coord1 - An array containing the [latitude, longitude] coordinates of the first point.
       * @param {Array} coord2 - An array containing the [latitude, longitude] coordinates of the second point.
       * @returns {number} The distance between the two points in kilometers.
       */
      const [lat1, lon1] = coord1;
      const [lat2, lon2] = coord2;

      const earthRadius = 3958.8; // Radius of the Earth in miles

      const dLat = ((lat2 - lat1) * Math.PI) / 180; // Convert latitude difference to radians
      const dLon = ((lon2 - lon1) * Math.PI) / 180; // Convert longitude difference to radians

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      const distance = earthRadius * c; // Distance in kilometers

      return Math.floor(distance);
    },
    copyToClipboard(contractor) {
      const text = `${contractor.name}\n${contractor.line1}, ${contractor.city}, ${contractor.state} ${contractor.zip}\n${contractor.phone}\n${contractor.email}`;
      this.$refs.copyElement.value = text;
      this.$refs.copyElement.select();
      document.execCommand('copy');
      this.addAlert('Copied contractor to clipboard.', 'low', 1);
    },
    resetSelectedContractors() {
      if (this.isPopupMode) {
        this.selectedContractors = {};
        this.contractors.forEach((contractor) => {
          this.selectedContractors[contractor.id] = false;
        });
        this.updateSelectedContractors();
      }
    },
    updateSelectedContractors() {
      if (this.isPopupMode) {
        const selectedContractorsList = this.contractors.filter((contractor) => this.selectedContractors[contractor.id]);
        this.$emit('update:modelValue', selectedContractorsList);
      }
    },
  },
  mounted() {
    document.getElementById('lookupInput').focus();
    this.resetSelectedContractors();
    if (this.initialAddress) {
      this.searchText = this.initialAddress;
      this.search();
    }
  },
  watch: {
    contractors() {
      this.resetSelectedContractors();
    },
  },
};
</script>

<style lang="scss" scoped>
.selection-column {
  .clipboard-icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    img {
      width: 1.5rem;
      height: 1.5rem;
    }

    #hover {
      display: none;
    }

    &:hover {
      #noHover {
        display: none;
      }

      #hover {
        display: block;
      }
    }
  }
}

.name-column {
  padding-left: 0.75rem !important;
}

#copyElement {
  position: fixed;
  bottom: 0;
  left: 0;
  opacity: 0;
}

.find-contractor-popup-close {
  margin-left: 2rem;
  cursor: pointer;
  align-self: flex-start;
}

.no-results {
  text-align: center;
}

.main-body.popup-mode {
  .title-bar {
    background-color: white;
  }

  table.full-width-table tr.table-header th,
  table.full-width-table tr.no-results {
    background-color: $appBackgroundColor;
  }
}
</style>
